import { Button } from './Button'

// ---------------------------------------------------------------------------
// BulkActionBar — selection action bar (props in → UI out).
//
// Appears above/within a list when rows are selected: a live count of the
// selection, a slot for the bulk actions the caller supplies (assign, change
// status, …), and a clear-selection control. Styling reuses the .filter-bar
// family from shared/styles/components.css — the same elevated, bordered bar
// the prototype mounts its bulk controls in — with the .filter-bar__sp spacer
// pushing the clear control to the trailing edge.
//
// Accessibility: the count lives in an aria-live="polite" region so a screen
// reader announces selection changes ("3 selected") without stealing focus.
//
// Dumb and surface-agnostic: no data fetching, no privileged logic, no
// Norwegian strings (count text + the clear label arrive already-i18n'd, the
// actions are caller-supplied children) — safe for the public, sessionless
// register bundle.
// ---------------------------------------------------------------------------

export interface BulkActionBarProps {
  /** Number of selected rows. The bar renders nothing when this is 0. */
  count: number
  /** Already-i18n'd selection-count label (e.g. "3 valgt"). */
  countLabel: string
  /** Clears the current selection. */
  onClear: () => void
  /** Already-i18n'd label for the clear control. */
  clearLabel: string
  /** The bulk-action controls (dropdowns, buttons) for the current selection. */
  children?: React.ReactNode
}

/**
 * Bulk-selection action bar.
 *
 * Usage:
 *   {selected.length > 0 && (
 *     <BulkActionBar
 *       count={selected.length}
 *       countLabel={t('admin.cases.bulk.selected_count', { count: selected.length })}
 *       onClear={() => setSelected([])}
 *       clearLabel={t('admin.cases.bulk.clear')}
 *     >
 *       <Dropdown … />
 *       <Dropdown … />
 *     </BulkActionBar>
 *   )}
 */
export function BulkActionBar({
  count,
  countLabel,
  onClear,
  clearLabel,
  children,
}: BulkActionBarProps) {
  if (count <= 0) return null

  return (
    <div className="filter-bar" data-testid="bulk-action-bar">
      <span className="muted" aria-live="polite">
        {countLabel}
      </span>
      {children}
      <span className="filter-bar__sp" />
      <Button variant="ghost" size="sm" icon="x" onClick={onClear}>
        {clearLabel}
      </Button>
    </div>
  )
}
